import React, { useState, useRef, useEffect, useReducer } from "react";
import JAKARTA_FONT_CSS from "./jakartaFont";

/* ============================================================
   PPC TRAINING SIMULATOR — 250 MWac / 325 MWdc, 345 kV POI
   Pre-scripted scenario playback, simplified IEEE 2800 / PRC-024
   ============================================================ */

const PAC = 250;          // MW ac rating
const PDC = 325;          // MWdc (1.3 ILR)
const SBASE = 262.5;      // MVA inverter apparent limit (1.05 pu)
const F0 = 60;

const C = {
  bg: "#f3f4f8", panel: "#ffffff", panel2: "#eef0f6", line: "#e8eaf1",
  text: "#1e2438", dim: "#6b7384", faint: "#aab1c2",
  freq: "#f59e0b", volt: "#14b8a6", p: "#4f7ef7", pAvail: "#9aa3b4",
  q: "#a855f7", lim: "#ef4444", ok: "#10b981", warn: "#f97316",
  accent: "#7166f0",
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const pw = (t, pts) => {
  if (t <= pts[0][0]) return pts[0][1];
  for (let i = 0; i < pts.length - 1; i++) {
    if (t <= pts[i + 1][0]) {
      const f = (t - pts[i][0]) / (pts[i + 1][0] - pts[i][0]);
      return pts[i][1] + f * (pts[i + 1][1] - pts[i][1]);
    }
  }
  return pts[pts.length - 1][1];
};

/* ---------------- Scenarios ---------------- */
const SCEN = {
  freq: [
    {
      id: "uf", name: "Grid generator trips — under-frequency", dur: 90,
      desc: "A large power station elsewhere on the grid trips offline. With that supply suddenly gone, system frequency sags to ~59.72 Hz, then governors and your plant's primary frequency response (PFR) arrest and restore it. Curtail your plant first (adjust Active power limit below) to create headroom and watch it respond upward.",
      grid: t => ({ f: pw(t, [[0,60],[10,60],[13,59.78],[18,59.72],[30,59.80],[55,59.93],[75,59.99],[90,60]]), v: 1.0, irr: 0.92 }),
      notes: [{ t: 10, text: "Unit trip — frequency falling" }, { t: 30, text: "Frequency arrested" }, { t: 75, text: "Recovery" }],
    },
    {
      id: "of", name: "Over-frequency excursion", dur: 80,
      desc: "Loss of a large load drives frequency high. The PPC must reduce active power per the droop curve — downward response is always available regardless of headroom.",
      grid: t => ({ f: pw(t, [[0,60],[12,60],[15,60.32],[22,60.38],[40,60.18],[60,60.04],[80,60]]), v: 1.0, irr: 0.92 }),
      notes: [{ t: 12, text: "Load rejection — frequency rising" }, { t: 60, text: "Recovery" }],
    },
    {
      id: "curt", name: "ISO curtailment order", dur: 200, rampDemo: true,
      desc: "The ISO issues a security-constrained dispatch down to 150 MW, then releases it. Curtailment executes fast for grid security; restoration follows the permitted ramp rate. Watch the reserve margin (headroom) the plant carries while curtailed.",
      grid: t => ({ f: 60, v: 1.0, irr: 0.95, agc: (t >= 15 && t < 80) ? 150 : Infinity }),
      notes: [{ t: 15, text: "ISO dispatch: limit 150 MW" }, { t: 80, text: "Curtailment released — ramped restoration" }],
    },
  ],
  volt: [
    {
      id: "sag", name: "Large load pickup — transmission sag", dur: 100,
      desc: "A large industrial load energizes on the interconnected 345 kV line and the system voltage sags to ~0.955 pu. Compare control modes: AVR and Volt-VAR inject MVAr to support the POI; fixed power factor does not.",
      grid: t => ({ f: 60, v: pw(t, [[0,1.0],[15,1.0],[20,0.955],[70,0.955],[76,0.998],[100,1.0]]), irr: 0.9 }),
      notes: [{ t: 15, text: "Load pickup — line voltage sagging" }, { t: 70, text: "Load served by new unit — voltage recovers" }],
    },
    {
      id: "rise", name: "Capacitor switching — voltage rise", dur: 80,
      desc: "A capacitor bank switches in and the voltage jumps to ~1.035 pu. This can be a utility bank on the nearby transmission system or one of the plant's own collector-substation/filter banks energizing — either injects reactive power that steps voltage up. Voltage-regulating modes absorb MVAr to pull the POI back toward setpoint.",
      grid: t => ({ f: 60, v: pw(t, [[0,1.0],[15,1.0],[15.5,1.035],[55,1.035],[56,1.005],[80,1.0]]), irr: 0.9 }),
      notes: [{ t: 15, text: "Cap bank in — voltage step up" }, { t: 55, text: "Cap bank out" }],
    },
    {
      id: "cloud", name: "Cloud front — irradiance ramp", dur: 100,
      desc: "A cloud front cuts plant output roughly in half just as the local voltage sags. In AVR/Volt-VAR mode the plant holds the POI up regardless of the MW drop. In power-factor mode reactive output follows MW down — so as the cloud cuts output the voltage is left unsupported and sags. Compare the POI trace in each mode.",
      grid: t => ({ f: 60, v: pw(t, [[0,1.0],[22,1.0],[32,0.965],[60,0.965],[78,1.0],[100,1.0]]), irr: pw(t, [[0,0.95],[20,0.95],[35,0.42],[60,0.42],[80,0.95],[100,0.95]]) }),
      notes: [{ t: 20, text: "Cloud front — output and voltage falling" }, { t: 60, text: "Clearing" }],
    },
  ],
  vrt: [
    {
      id: "f3", name: "3-phase fault — 0.20 pu, 100 ms (6 cycles)", dur: 40,
      desc: "A close-in transmission fault collapses voltage to 0.20 pu for 6 cycles before breakers clear it. The plant must ride through inside the PRC-024 'no-trip' envelope, injecting reactive current (K-factor) to support voltage, then recover active power.",
      grid: t => ({ f: 60, v: pw(t, [[0,1],[10,1],[10.01,0.20],[10.11,0.20],[10.12,0.88],[12,0.97],[16,1.0],[40,1.0]]), irr: 0.92 }),
      eventT: 10,
      notes: [{ t: 10, text: "FAULT — voltage collapse" }, { t: 10.11, text: "Breakers clear fault" }],
    },
    {
      id: "dep", name: "Depressed voltage — 0.80 pu, delayed clearing (trip expected)", dur: 45,
      desc: "A remote fault with slow clearing holds voltage at 0.80 pu. That's above the deep-fault floor, but holding below ~0.90 pu for more than ~3 s falls outside the continuous-operation envelope — so the plant rides through as long as required, then correctly trips. Watch the rising envelope step up to 0.90 pu and catch the trace at ~3 s.",
      grid: t => ({ f: 60, v: pw(t, [[0,1],[10,1],[10.05,0.80],[14,0.80],[14.3,0.96],[18,1],[45,1]]), irr: 0.92 }),
      eventT: 10,
      notes: [{ t: 10, text: "Remote fault — depressed to 0.80 pu" }, { t: 13, text: "~3 s below 0.90 pu — envelope exceeded, plant trips" }],
    },
    {
      id: "swl", name: "Voltage swell — 1.16 pu (ride-through)", dur: 40,
      desc: "Load rejection causes a brief over-voltage peaking near 1.16 pu. The plant absorbs reactive current to pull its terminal down and stays below the high-voltage envelope's decreasing steps — riding through as required. Watch the POI trace stay under the dashed HV boundary the whole time.",
      grid: t => ({ f: 60, v: pw(t, [[0,1],[10,1],[10.05,1.16],[10.35,1.16],[10.7,1.04],[16,1.01],[40,1]]), irr: 0.92 }),
      eventT: 10,
      notes: [{ t: 10, text: "Load rejection — over-voltage to 1.16 pu" }, { t: 11, text: "Voltage recovering — plant rides through" }],
    },
    {
      id: "deep", name: "Stuck breaker — 0.30 pu deep fault (trip expected)", dur: 40,
      desc: "A breaker fails to clear a deep 0.30 pu fault. At that depth the PRC-024 envelope only requires 0.15 s of ride-through — the plant holds for that, then trips, long before the delayed backup protection clears the fault at 2.5 s. Tripping here is correct, compliant behavior.",
      grid: t => ({ f: 60, v: pw(t, [[0,1],[10,1],[10.01,0.30],[12.5,0.30],[12.6,0.95],[16,1],[40,1]]), irr: 0.92 }),
      eventT: 10,
      notes: [{ t: 10, text: "FAULT — breaker failure (0.30 pu)" }, { t: 10.2, text: "0.15 s ride-through limit exceeded — plant trips" }, { t: 12.5, text: "Backup clears fault (plant already offline)" }],
    },
  ],
  frt: [
    {
      id: "uf2", name: "Under-frequency — 59.3 Hz low point", dur: 90,
      desc: "A severe generation loss drives frequency to 59.3 Hz. The plant must remain connected (well inside the PRC-024 frequency envelope) and deliver PFR if it has headroom.",
      grid: t => ({ f: pw(t, [[0,60],[10,60],[14,59.45],[19,59.30],[35,59.55],[60,59.85],[85,60],[90,60]]), v: 1.0, irr: 0.92 }),
      notes: [{ t: 10, text: "Major generation loss" }, { t: 19, text: "Frequency low point 59.30 Hz" }],
    },
    {
      id: "of2", name: "Over-frequency — 60.8 Hz", dur: 80,
      desc: "An islanding-type event pushes frequency to 60.8 Hz. The plant backs down on droop and must stay connected within the high-frequency envelope.",
      grid: t => ({ f: pw(t, [[0,60],[10,60],[13,60.65],[18,60.80],[35,60.45],[60,60.1],[80,60]]), v: 1.0, irr: 0.92 }),
      notes: [{ t: 10, text: "Islanding event — frequency rising" }],
    },
    {
      id: "sev", name: "Severe decline — 57.6 Hz (trip expected)", dur: 60,
      desc: "Cascading outages drive frequency below 57.8 Hz — outside the no-trip envelope. The plant rides through the decline as required, then trips when the boundary is crossed.",
      grid: t => ({ f: pw(t, [[0,60],[8,60],[14,58.6],[22,57.6],[30,57.6],[40,58.8],[60,59.5]]), v: 1.0, irr: 0.92 }),
      notes: [{ t: 8, text: "Cascading outages" }, { t: 22, text: "Frequency below 57.8 Hz" }],
    },
  ],
  day: [
    {
      id: "gd", name: "A day on the grid — combined sequence", dur: 240, rampDemo: true,
      desc: "A storyline combining everything: morning curtailment order → release → large load pickup sags the line voltage → a transmission fault and ride-through → frequency event with PFR. Set your control modes, press play, and watch the PPC manage all of it.",
      grid: t => ({
        f: pw(t, [[0,60],[180,60],[185,59.75],[195,59.82],[215,59.95],[240,60]]),
        v: pw(t, [[0,1],[95,1],[100,0.957],[140,0.957],[145,0.998],[150,1],[160,1],[160.01,0.25],[160.13,0.25],[160.14,0.9],[163,1],[240,1]]),
        irr: pw(t, [[0,0.88],[120,0.95],[240,0.9]]),
        agc: (t >= 20 && t < 75) ? 160 : Infinity,
      }),
      eventT: 160,
      notes: [
        { t: 20, text: "ISO curtailment: 160 MW" }, { t: 75, text: "Curtailment released" },
        { t: 100, text: "Load pickup — voltage sag" }, { t: 145, text: "Voltage recovers" },
        { t: 160, text: "Transmission fault — ride-through" }, { t: 185, text: "Grid generator trips — PFR responding" },
      ],
    },
  ],
  pid: [
    {
      id: "offset", name: "Proportional only — offset never clears", dur: 40,
      desc: "A pure proportional (P) controller responds to a setpoint step but settles short of target — proportional action alone always leaves a steady-state error. Add integral action (Ki) and watch the gap close.",
      preset: { loop: "power", mode: "P", kp: 2.0, ki: 0, kd: 0, noise: 0.005, delay: 0.1 },
      notes: [{ t: 2, text: "Setpoint step applied" }, { t: 12, text: "Output stalls below target — P alone can't close the gap" }, { t: 22, text: "Switch to PI and raise Ki to remove the offset" }],
    },
    {
      id: "sluggish", name: "Sluggish — gains too low", dur: 40,
      desc: "A PI controller with gains set too low. It reaches target with no overshoot, but takes far too long — slow rise and settling. Raise Kp (and Ki) to speed it up without overshooting.",
      preset: { loop: "power", mode: "PI", kp: 0.4, ki: 0.15, kd: 0, noise: 0.005, delay: 0.1 },
      notes: [{ t: 2, text: "Setpoint step applied" }, { t: 20, text: "Still crawling toward target — raise Kp/Ki" }],
    },
    {
      id: "tuned", name: "Well-tuned PI — the target", dur: 40,
      desc: "A well-tuned PI controller: fast rise, small overshoot, settles quickly with zero steady-state error. This is what you're aiming for. Use it as the reference, then compare the other scenarios.",
      preset: { loop: "power", mode: "PI", kp: 1.6, ki: 0.8, kd: 0, noise: 0.005, delay: 0.1 },
      notes: [{ t: 2, text: "Setpoint step applied" }, { t: 10, text: "Settled — crisp response, no offset" }],
    },
    {
      id: "overshoot", name: "Aggressive — overshoot & ringing", dur: 40,
      desc: "Gains pushed too high. The voltage shoots past setpoint and rings before settling. Overshoot stresses equipment and annoys the grid operator. Lower Kp and Ki until the overshoot is acceptable.",
      preset: { loop: "voltage", mode: "PI", kp: 4.0, ki: 3.0, kd: 0, noise: 0.005, delay: 0.18 },
      notes: [{ t: 2, text: "Setpoint step applied" }, { t: 6, text: "Overshoot and ringing — back off the gains" }],
    },
    {
      id: "oscillatory", name: "Marginally stable — sustained oscillation", dur: 40,
      desc: "Too much gain plus loop delay puts the controller on the edge of stability — it oscillates and barely settles. This is 'hunting,' a classic field complaint. Reduce Kp to pull it back to stable.",
      preset: { loop: "voltage", mode: "PI", kp: 5.5, ki: 3.2, kd: 0, noise: 0.005, delay: 0.25 },
      notes: [{ t: 2, text: "Setpoint step applied" }, { t: 15, text: "Sustained oscillation (hunting) — lower Kp" }],
    },
    {
      id: "unstable", name: "Unstable — gain fighting dead-time", dur: 40,
      desc: "High gain combined with significant communication delay drives the loop unstable: the oscillation grows until the command slams against its limits. Delay is the enemy of high gain. Cut the gain (or the delay) to recover control.",
      preset: { loop: "power", mode: "PI", kp: 5.0, ki: 2.5, kd: 0, noise: 0.005, delay: 0.5 },
      notes: [{ t: 2, text: "Setpoint step applied" }, { t: 10, text: "Oscillation growing — command hitting limits" }, { t: 20, text: "Reduce Kp/Ki or the comms delay to stabilize" }],
    },
    {
      id: "dnoise", name: "Derivative on a noisy signal — why PI, not PID", dur: 40,
      desc: "This is why solar PPCs use PI, not full PID. The derivative term reacts to the rate of change of a noisy measurement, so it amplifies the noise into violent command chatter — wearing actuators and destabilizing the loop. Drag Kd to zero (switch to PI) and the command goes clean instantly.",
      preset: { loop: "voltage", mode: "PID", kp: 1.6, ki: 0.8, kd: 0.8, noise: 0.035, delay: 0.15 },
      notes: [{ t: 2, text: "Setpoint step applied" }, { t: 8, text: "Derivative term amplifying noise — command chattering" }, { t: 16, text: "Set Kd = 0 (use PI) and watch it settle cleanly" }],
    },
    {
      id: "windup", name: "Integral windup — rate-limited actuator", dur: 40,
      desc: "The actuator can only ramp so fast (a slew-rate limit, like the plant's MW ramp rate). While it catches up, the integral keeps piling up error — then drives a big overshoot when it finally arrives. That's integral windup. Switch Anti-windup ON (or lower Ki) and the overshoot disappears.",
      preset: { loop: "power", mode: "PI", kp: 1.5, ki: 1.5, kd: 0, noise: 0.005, delay: 0.1, rate: 0.1, aw: false },
      notes: [{ t: 2, text: "Setpoint step applied" }, { t: 8, text: "Command slew-limited — integral winding up" }, { t: 14, text: "Large overshoot from windup — turn Anti-windup ON to fix" }],
    },
  ],
};

/* PRC-024-style envelopes (simplified) */
const LV_BANDS = [[0.45, 0.15], [0.65, 0.30], [0.75, 2.0], [0.90, 3.0]];
const HV_BANDS = [[1.20, 0.0], [1.175, 0.20], [1.15, 0.50], [1.10, 1.0]];
const FL_BANDS = [[57.8, 0.0], [58.4, 30], [59.4, 180]];
const FH_BANDS = [[61.8, 0.0], [61.6, 30], [60.6, 180]];

/* Build a ride-through boundary polyline directly from the SAME bands the
   protection trips on, so the drawn line can never drift from enforcement.
   Each [thr, lim] means "you may stay past thr only until lim seconds after the
   event" — so the boundary holds the previous level until lim, then steps to thr.
   `edge` is the pre-deadline level (chart floor for LV, ceiling for HV). */
function envFromBands(bands, eventT, xMax, edge) {
  const pts = [[eventT, edge]];
  let prev = edge;
  for (const [thr, lim] of bands) {
    pts.push([eventT + lim, prev], [eventT + lim, thr]);
    prev = thr;
  }
  pts.push([xMax, prev]);
  return pts;
}

/* ---------------- Simulation ---------------- */
function initSim(scen, gc, ctl) {
  const g = scen.grid(0);
  const SCC = gc.scr * PAC;
  const qMaxGC = PAC * Math.tan(Math.acos(Math.min(gc.pfReq, 0.9999)));
  const pAvail = Math.min(PAC, g.irr * PDC * 0.97);
  const p0 = clamp(Math.min(pAvail, ctl.limit, g.agc ?? Infinity), 0, PAC);
  let q0 = 0;
  if (ctl.mode === "AVR" || ctl.mode === "VoltVAR") q0 = (ctl.vset - g.v) * SCC;
  else if (ctl.mode === "PF") q0 = (ctl.pfLead ? -1 : 1) * p0 * Math.tan(Math.acos(clamp(Math.abs(ctl.pfset), 0.85, 1)));
  else q0 = ctl.qset;
  q0 = clamp(q0, -qMaxGC, qMaxGC);
  const vPoi = g.v + q0 / SCC;
  return {
    t: 0, pDisp: p0, p: p0, q: q0, qInt: q0, vPoi, tripped: false, tripT: null, done: false,
    timers: {}, flags: {}, lastSample: 0, noteIdx: 0, log: [],
    hist: [{
      t: 0, f: g.f, vSrc: g.v, vPoi, p: p0, pAvail, q: q0, qCap: qMaxGC,
      limit: Math.min(ctl.limit, PAC), reserve: Math.max(0, pAvail - p0),
      iq: q0 / Math.max(0.2, vPoi) / PAC, ip: p0 / Math.max(0.2, vPoi) / PAC,
    }],
  };
}

function stepSim(s, dt, scen, gc, ctl) {
  if (s.done) return;
  s.t += dt;
  if (s.t >= scen.dur) { s.t = scen.dur; s.done = true; }
  const g = scen.grid(s.t);
  const SCC = gc.scr * PAC;
  const qMaxGC = PAC * Math.tan(Math.acos(Math.min(gc.pfReq, 0.9999)));
  const pAvail = Math.min(PAC, g.irr * PDC * 0.97);

  const pushLog = (text, kind = "info") => s.log.push({ t: s.t, text, kind });

  // scenario notes
  while (s.noteIdx < (scen.notes || []).length && scen.notes[s.noteIdx].t <= s.t) {
    pushLog(scen.notes[s.noteIdx].text, "scen"); s.noteIdx++;
  }

  /* ---- Active power ---- */
  const agc = g.agc ?? Infinity;
  const limit = Math.min(ctl.limit, agc);
  if (agc < PAC && !s.flags.agc) { s.flags.agc = true; pushLog(`Curtailment order received — dispatch target ${agc} MW`, "warn"); }
  if (!(agc < PAC) && s.flags.agc) { s.flags.agc = false; pushLog("Curtailment released — returning to available power", "ok"); }

  const pBase = Math.min(pAvail, limit);
  const rate = gc.ramp / 60; // MW/s, restoration/dispatch ramp
  const downRate = Math.max(rate, 5); // security curtailments execute fast (≥300 MW/min)
  s.pDisp += clamp(pBase - s.pDisp, -downRate * dt, rate * dt);
  s.pDisp = clamp(s.pDisp, 0, PAC);

  // PFR (droop with deadband) — fast path, not subject to dispatch ramp
  const df = g.f - F0, db = gc.deadband / 1000;
  let dP = 0;
  if (Math.abs(df) > db) {
    const eff = df > 0 ? df - db : df + db;
    dP = -(eff / F0) / (gc.droop / 100) * PAC;
  }
  const pfrActive = Math.abs(dP) > 0.5;
  if (pfrActive && !s.flags.pfr) { s.flags.pfr = true; pushLog(`Deadband exceeded — PFR ${dP > 0 ? "increasing" : "reducing"} output (droop ${gc.droop}%)`, "warn"); }
  if (!pfrActive && s.flags.pfr) { s.flags.pfr = false; pushLog("Frequency inside deadband — PFR released", "ok"); }
  if (dP > 0 && s.pDisp + dP > pAvail && !s.flags.hr) { s.flags.hr = true; pushLog("PFR limited — insufficient headroom (plant at available power)", "warn"); }
  if (s.flags.hr && (dP <= 0 || s.pDisp + dP <= pAvail)) s.flags.hr = false;

  let pTar = clamp(s.pDisp + dP, 0, dP > 0 ? pAvail : PAC);

  /* ---- Reactive power command ---- */
  let qCmd = 0;
  const vMeas = s.vPoi; // measured POI from last step
  if (ctl.mode === "AVR") {
    s.qInt += (ctl.vset - vMeas) * SCC * 1.6 * dt;
    s.qInt = clamp(s.qInt, -qMaxGC, qMaxGC);
    qCmd = s.qInt;
  } else if (ctl.mode === "VoltVAR") {
    const dv = vMeas - ctl.vset, dbv = ctl.vvDb;
    if (Math.abs(dv) > dbv) {
      const eff = dv > 0 ? dv - dbv : dv + dbv;
      qCmd = clamp(-(eff / ctl.vvDroop) * qMaxGC, -qMaxGC, qMaxGC);
    }
  } else if (ctl.mode === "PF") {
    const sgn = ctl.pfLead ? -1 : 1;
    qCmd = sgn * s.p * Math.tan(Math.acos(clamp(Math.abs(ctl.pfset), 0.85, 1)));
  } else { // Fixed Q
    qCmd = ctl.qset;
  }
  const qCap = Math.min(qMaxGC, Math.sqrt(Math.max(0, SBASE * SBASE - pTar * pTar)));
  const qSat = Math.abs(qCmd) > qCap - 0.5 && (ctl.mode === "AVR" || ctl.mode === "VoltVAR");
  if (qSat && !s.flags.qsat) { s.flags.qsat = true; pushLog("Reactive limit reached — voltage regulator saturated at capability", "warn"); }
  if (!qSat && s.flags.qsat) s.flags.qsat = false;
  qCmd = clamp(qCmd, -qCap, qCap);

  /* ---- Fault / ride-through dynamics ---- */
  let iq = 0, ipLimP = Infinity;
  const vPre = g.v + s.q / SCC;
  let faultLV = vPre < 0.85, faultHV = vPre > 1.10;
  if (faultLV) {
    iq = clamp(gc.vrtK * (0.88 - vPre), 0, 1.05);
    qCmd = iq * vPre * PAC;
    const ipMax = Math.sqrt(Math.max(0, 1.05 * 1.05 - iq * iq));
    ipLimP = ipMax * vPre * PAC;
    if (!s.flags.flt) { s.flags.flt = true; pushLog(`Low-voltage detected (${vPre.toFixed(2)} pu) — injecting reactive current, K=${gc.vrtK}`, "warn"); }
  } else if (faultHV) {
    iq = -clamp(gc.vrtK * (vPre - 1.10), 0, 1.05);
    qCmd = iq * vPre * PAC;
    if (!s.flags.flt) { s.flags.flt = true; pushLog(`Over-voltage detected (${vPre.toFixed(2)} pu) — absorbing reactive current`, "warn"); }
  } else if (s.flags.flt) {
    s.flags.flt = false; pushLog("Voltage recovered — resuming normal control, restoring active power", "ok");
  }

  /* ---- Apply dynamics ---- */
  const tauP = faultLV ? 0.08 : 1.4, tauQ = (faultLV || faultHV) ? 0.08 : 0.5;
  if (!s.tripped) {
    let pNext = s.p + (Math.min(pTar, ipLimP) - s.p) * Math.min(1, dt / tauP);
    s.p = clamp(pNext, 0, PAC);
    s.q = s.q + (qCmd - s.q) * Math.min(1, dt / tauQ);
  } else { s.p = 0; s.q = 0; }

  s.vPoi = g.v + (s.tripped ? 0 : s.q) / SCC;

  /* ---- Protection (PRC-024 simplified) ---- */
  const tmr = s.timers;
  const checkBands = (val, bands, low, label) => {
    const isF = label[0] === "f";
    let bind = null; // most restrictive band currently exceeded
    for (const [thr, lim] of bands) {
      const key = label + thr;
      const inBand = low ? val < thr : val > thr;
      tmr[key] = inBand ? (tmr[key] || 0) + dt : 0;
      if (inBand && tmr[key] > lim && !s.tripped) {
        s.tripped = true;
        s.tripT = s.t;
        pushLog(`TRIP — ${isF ? "frequency" : "voltage"} outside PRC-024 no-trip envelope (${val.toFixed(isF ? 2 : 3)} ${isF ? "Hz" : "pu"} > ${lim}s)`, "trip");
      }
      if (inBand && (bind === null || lim < bind[1])) bind = [thr, lim];
    }
    // Announce frequency ride-through timers as they arm, escalate, and clear (PRC-024 tiers)
    if (isF) {
      const af = "arm" + label, cur = bind ? bind[0] : null;
      if (cur !== (s.flags[af] ?? null) && !s.tripped) {
        if (bind) {
          pushLog(bind[1] <= 0
            ? `Frequency ${val.toFixed(2)} Hz — past ${bind[0]} Hz instantaneous trip limit`
            : `Frequency ${val.toFixed(2)} Hz — past ${bind[0]} Hz; ride-through limited, trip permitted after ${bind[1]}s`, "warn");
        } else if (s.flags[af] != null) {
          pushLog("Frequency back inside continuous range — ride-through timer cleared", "ok");
        }
        s.flags[af] = cur;
      }
    }
  };
  if (!s.tripped) {
    checkBands(s.vPoi, LV_BANDS, true, "lv");
    checkBands(s.vPoi, HV_BANDS, false, "hv");
    checkBands(g.f, FL_BANDS, true, "fl");
    checkBands(g.f, FH_BANDS, false, "fh");
  }

  /* ---- Record ---- */
  // Dense sampling around a fault/event so fast transients render crisply; coarse elsewhere.
  const nearEvt = scen.eventT != null && s.t >= scen.eventT - 1 && s.t <= scen.eventT + 4;
  const sampleEvery = nearEvt ? 0.01 : scen.dur / 700;
  if (s.t - s.lastSample >= sampleEvery || s.done) {
    s.lastSample = s.t;
    const p = s.p, q = s.q;
    s.hist.push({
      t: s.t, f: g.f, vSrc: g.v, vPoi: s.vPoi, p, pAvail, q,
      qCap, limit: Math.min(limit, PAC), reserve: Math.max(0, pAvail - p),
      pf: p > 1 ? p / Math.sqrt(p * p + q * q) * (q < 0 ? -1 : 1) : (q < 0 ? -1 : 1),
      iq: Math.abs(q) / Math.max(0.2, s.vPoi) / PAC * Math.sign(q),
      ip: p / Math.max(0.2, s.vPoi) / PAC,
    });
  }
}

/* ============================================================
   PID TUNING TAB — self-contained control-loop simulation.
   A normalized parallel PID drives a first-order plant (with a
   communication dead-time and measurement noise) to a setpoint.
   The SAME controller math serves both loops: the voltage loop
   (command = MVAr → POI volts via ΔV = Q/SCC) and the active-power
   loop (command = MW). The plant DC gain is 1 in normalized space
   for both loops, so identical Kp/Ki/Kd produce identical overshoot/
   oscillation signatures. Kept fully separate from stepSim so the
   other tabs' physics are never touched.
   ============================================================ */
const TAU_PID = 1.2;            // process first-order lag (s)
const SCC_PID = 750;           // tab-local short-circuit capacity for the voltage loop (weak grid)
const VSPAN = 0.10;            // voltage normalization half-span (pu): ±VSPAN ↔ ±1 normalized
const QSCALE = VSPAN * SCC_PID; // MVAr per normalized command unit (= 75)
const Q_PID_CAP = 82;          // MVAr actuator cap (voltage loop)

// Per-loop descriptor: setpoint step, physical<->normalized maps, display ranges.
function pidCfg(loop) {
  if (loop === "power") {
    return {
      loop, spA: 120, spB: 160, stepT: 2,
      pvMin: 0, pvMax: 270, pvUnit: " MW", pvFmt: v => v.toFixed(0),
      uMinPhys: 0, uMaxPhys: 250, uUnit: " MW", uFmt: v => v.toFixed(0),
      uClampN: [0, 1.0], color: C.p,
      nPV: v => v / PAC, pPV: n => n * PAC, pU: n => n * PAC,
    };
  }
  return { // voltage
    loop, spA: 1.00, spB: 1.02, stepT: 2,
    pvMin: 0.96, pvMax: 1.06, pvUnit: " pu", pvFmt: v => v.toFixed(3),
    uMinPhys: -Q_PID_CAP, uMaxPhys: Q_PID_CAP, uUnit: " MVAr", uFmt: v => v.toFixed(0),
    uClampN: [-Q_PID_CAP / QSCALE, Q_PID_CAP / QSCALE], color: C.volt,
    nPV: v => (v - 1.0) / VSPAN, pPV: n => 1.0 + n * VSPAN, pU: n => n * QSCALE,
  };
}

// Normalized setpoint at time t (shared step shape, slight ramp avoids a 1-sample vertical).
function pidSpN(cfg, t) {
  return cfg.nPV(pw(t, [[0, cfg.spA], [cfg.stepT, cfg.spA], [cfg.stepT + 0.25, cfg.spB], [1e9, cfg.spB]]));
}

// Dead-time buffer lookup: interpolate the command at a past SIM TIME (not step index),
// so the effective delay is independent of playback speed / sub-stepping.
function bufLookup(buf, tq) {
  if (tq <= buf[0].t) return buf[0].u;
  for (let i = buf.length - 1; i >= 1; i--) {
    if (buf[i - 1].t <= tq) {
      const a = buf[i - 1], b = buf[i];
      const f = (tq - a.t) / Math.max(1e-6, b.t - a.t);
      return a.u + f * (b.u - a.u);
    }
  }
  return buf[0].u;
}

function recordPID(s, cfg, spN, plantN, pvMeasN, uN, P, iContrib, D) {
  s.hist.push({
    t: s.t,
    sp: cfg.pPV(spN), pv: cfg.pPV(plantN), pvMeas: cfg.pPV(pvMeasN), u: cfg.pU(uN),
    pTerm: cfg.pU(P), iTerm: cfg.pU(iContrib), dTerm: cfg.pU(D),
    error: cfg.pPV(spN) - cfg.pPV(pvMeasN), uMin: cfg.uMinPhys, uMax: cfg.uMaxPhys,
  });
}

function initPID(scen, pid) {
  const cfg = pidCfg(pid.loop);
  const spN0 = cfg.nPV(cfg.spA);   // initial normalized setpoint
  const s = {
    t: 0, plantN: spN0, iTerm: 0, pvMeasPrev: spN0, bias: spN0, uPrev: spN0,
    buf: [{ t: 0, u: spN0 }],
    done: false, lastSample: 0, noteIdx: 0, log: [], hist: [],
  };
  recordPID(s, cfg, spN0, spN0, spN0, spN0, 0, 0, 0);
  return s;
}

function stepPID(s, dt, scen, pid) {
  if (s.done) return;
  const cfg = pidCfg(pid.loop);
  s.t += dt;

  while (s.noteIdx < (scen.notes ? scen.notes.length : 0) && s.t >= scen.notes[s.noteIdx].t) {
    s.log.push({ t: s.t, text: scen.notes[s.noteIdx].text, kind: "scen" });
    s.noteIdx++;
  }

  const spN = pidSpN(cfg, s.t);
  const pvMeasN = s.plantN + pid.noise * (Math.random() - 0.5) * 2;   // ± noise (normalized)

  // Selected controller type zeroes the unused terms.
  const ki = pid.mode === "P" ? 0 : pid.ki;
  const kd = pid.mode === "PID" ? pid.kd : 0;
  const aw = pid.aw !== false;           // anti-windup on unless explicitly disabled
  const rate = pid.rate || 0;            // command slew-rate limit (normalized /s); 0 = off
  const e = spN - pvMeasN;
  const P = pid.kp * e;
  if (ki > 0) s.iTerm += ki * e * dt;
  const iContrib = ki > 0 ? s.iTerm : 0;
  const D = kd > 0 ? -kd * (pvMeasN - s.pvMeasPrev) / dt : 0;
  const uRaw = s.bias + P + iContrib + D;
  let uN = clamp(uRaw, cfg.uClampN[0], cfg.uClampN[1]);                  // actuator magnitude limit
  if (rate > 0) uN = clamp(uN, s.uPrev - rate * dt, s.uPrev + rate * dt); // actuator slew-rate limit
  if (aw && ki > 0 && uN !== uRaw) s.iTerm -= ki * e * dt;   // conditional anti-windup (magnitude or rate limited)
  s.pvMeasPrev = pvMeasN;
  s.uPrev = uN;

  // Plant: comms dead-time, then first-order lag (all normalized).
  s.buf.push({ t: s.t, u: uN });
  const tCut = s.t - (pid.delay + 0.5);
  while (s.buf.length > 2 && s.buf[1].t < tCut) s.buf.shift();
  const uDelayed = bufLookup(s.buf, s.t - pid.delay);
  s.plantN += (uDelayed - s.plantN) * Math.min(1, dt / TAU_PID);

  if (s.t >= scen.dur) { s.t = scen.dur; s.done = true; }

  const sampleEvery = scen.dur / 900;
  if (s.t - s.lastSample >= sampleEvery || s.done) {
    s.lastSample = s.t;
    recordPID(s, cfg, spN, s.plantN, pvMeasN, uN, P, iContrib, D);
  }
}

// Step-response metrics from the PID history (computed on the true PV, not the noisy measurement).
function pidMetrics(hist) {
  if (!hist || hist.length < 3) return null;
  const pv0 = hist[0].pv, spFinal = hist[hist.length - 1].sp, delta = spFinal - pv0;
  if (Math.abs(delta) < 1e-6) return null;
  const sgn = delta >= 0 ? 1 : -1, mag = Math.abs(delta);
  let t0 = hist[0].t;
  for (const h of hist) { if (Math.abs(h.sp - spFinal) <= mag * 0.5) { t0 = h.t; break; } }
  let t10 = null, t90 = null;
  for (const h of hist) {
    const prog = (h.pv - pv0) * sgn;
    if (t10 === null && prog >= mag * 0.1) t10 = h.t;
    if (t90 === null && prog >= mag * 0.9) { t90 = h.t; break; }
  }
  const rise = (t10 !== null && t90 !== null) ? t90 - t10 : null;
  let peak = -Infinity;
  for (const h of hist) peak = Math.max(peak, (h.pv - spFinal) * sgn);
  const overshoot = Math.max(0, peak) / mag * 100;
  const band = mag * 0.02;
  let settleT = t0;
  for (let i = hist.length - 1; i >= 0; i--) { if (Math.abs(hist[i].pv - spFinal) > band) { settleT = hist[i].t; break; } }
  const settled = Math.abs(hist[hist.length - 1].pv - spFinal) <= band;
  const n = Math.max(1, Math.floor(hist.length * 0.1));
  let sum = 0; for (let i = hist.length - n; i < hist.length; i++) sum += hist[i].pv;
  const ssErr = spFinal - sum / n;
  return { overshoot, rise, settle: settleT - t0, settled, ssErr };
}

/* ---------------- Chart ---------------- */
// Pick a round time-axis division (~6 ticks across the span) for legible gridlines.
function niceTimeStep(span) {
  const target = span / 6;
  for (const c of [5, 10, 15, 20, 30, 40, 60, 90, 120]) if (c >= target) return c;
  return 120;
}
function Chart({ title, unit, xMax, yMin, yMax, hist, series, hLines = [], bands = [], markers = [], height = 150, env = null, envs = null, fmt = v => v.toFixed(2), info = null }) {
  const W = 640, H = height, pl = 46, pr = 10, pt = 8, pb = 18;
  const sx = t => pl + (t / xMax) * (W - pl - pr);
  const sy = v => pt + (1 - (clamp(v, yMin, yMax) - yMin) / (yMax - yMin)) * (H - pt - pb);
  const step = Math.max(1, Math.ceil(hist.length / 1500));
  const paths = series.map(sr => {
    let d = "";
    for (let i = 0; i < hist.length; i += step) {
      const h = hist[i];
      d += (d ? "L" : "M") + sx(h.t).toFixed(1) + "," + sy(h[sr.key]).toFixed(1);
    }
    return d;
  });
  const yTicks = [];
  for (let i = 0; i <= 4; i++) yTicks.push(yMin + (i / 4) * (yMax - yMin));
  const xStep = niceTimeStep(xMax);
  const xTicks = [{ v: 0, a: "start" }];
  for (let v = xStep; v < xMax - xStep * 0.5; v += xStep) xTicks.push({ v, a: "middle" });
  xTicks.push({ v: xMax, a: "end" });
  const last = hist[hist.length - 1];
  return (
    <div className="chart">
      <div className="chartHead">
        <span className="chartTitle">{title}{info && (
          <span className="infoDot chartInfo" tabIndex={0}>i<span className="tip">{info}</span></span>
        )}</span>
        <span className="legend">
          {series.map(sr => (
            <span key={sr.key} className="legItem">
              <span className="swatch" style={{ background: sr.color, opacity: sr.dash ? 0.7 : 1 }} />
              {sr.label}
              <b style={{ color: sr.color }}>{last ? fmt(last[sr.key]) : "—"}{unit}</b>
            </span>
          ))}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="chartSvg" preserveAspectRatio="none">
        {bands.map((b, i) => (
          <rect key={i} x={pl} width={W - pl - pr} y={sy(b[1])} height={Math.max(0, sy(b[0]) - sy(b[1]))} fill={b[2]} />
        ))}
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={pl} x2={W - pr} y1={sy(v)} y2={sy(v)} stroke={C.line} strokeWidth="1" />
            <text x={pl - 5} y={sy(v) + 3} textAnchor="end" className="tick">{fmt(v)}</text>
          </g>
        ))}
        {xTicks.map((tk, i) => (
          <g key={"xt" + i}>
            <line x1={sx(tk.v)} x2={sx(tk.v)} y1={pt} y2={H - pb} stroke={C.line} strokeWidth="1" />
            <text x={sx(tk.v)} y={H - 4} textAnchor={tk.a} className="tick">{tk.v}s</text>
          </g>
        ))}
        {markers.map((m, i) => (
          <g key={"m" + i} opacity={last && last.t >= m.t ? 1 : 0.25}>
            <line x1={sx(m.t)} x2={sx(m.t)} y1={pt} y2={H - pb}
              stroke={m.trip ? C.lim : C.faint} strokeWidth={m.trip ? 1.5 : 1}
              strokeDasharray={m.trip ? "none" : "3 4"} />
            {m.trip && <text x={sx(m.t) + 3} y={pt + 9} className="tick" fill={C.lim}>TRIP</text>}
          </g>
        ))}
        {(envs || (env ? [env] : [])).filter(Boolean).map((e, k) => (
          <path key={"env" + k} d={e.map((p2, i) => (i ? "L" : "M") + sx(p2[0]) + "," + sy(p2[1])).join("")}
            stroke={C.lim} strokeWidth="1.5" strokeDasharray="6 3" fill="none" />
        ))}
        {hLines.map((h2, i) => (
          <g key={"h" + i}>
            <line x1={pl} x2={W - pr} y1={sy(h2.v)} y2={sy(h2.v)} stroke={h2.color || C.lim} strokeDasharray="5 4" strokeWidth="1.2" />
            {h2.label && <text x={W - pr - 3} y={sy(h2.v) - 3} textAnchor="end" className="tick" fill={h2.color || C.lim}>{h2.label}</text>}
          </g>
        ))}
        {paths.map((d, i) => (
          <path key={i} d={d} fill="none" stroke={series[i].color} strokeWidth={series[i].w || 1.8}
            strokeDasharray={series[i].dash ? "5 4" : "none"} strokeLinejoin="round" />
        ))}
        {last && <circle cx={sx(last.t)} cy={sy(last[series[0].key])} r="3" fill={series[0].color} />}
      </svg>
    </div>
  );
}

/* ---------------- One-line diagram ---------------- */
function SLD({ p, q, vPoi, tripped, f }) {
  const flow = p > 1;
  const dur = clamp(3 - (p / PAC) * 2.4, 0.5, 3);
  return (
    <div className="sld">
      <svg viewBox="0 0 560 64" className="sldSvg">
        <g className="sldLabels">
          <rect x="6" y="18" width="58" height="28" rx="3" fill="none" stroke={C.dim} />
          <text x="35" y="30" textAnchor="middle" className="sldT">PV ARRAY</text>
          <text x="35" y="41" textAnchor="middle" className="sldS">325 MWdc</text>
          <rect x="120" y="18" width="58" height="28" rx="3" fill="none" stroke={C.dim} />
          <text x="149" y="30" textAnchor="middle" className="sldT">INVERTERS</text>
          <text x="149" y="41" textAnchor="middle" className="sldS">250 MWac</text>
          <circle cx="248" cy="27" r="9" fill="none" stroke={C.dim} />
          <circle cx="248" cy="37" r="9" fill="none" stroke={C.dim} />
          <text x="248" y="60" textAnchor="middle" className="sldS">GSU 34.5/345 kV</text>
          <rect x="330" y="24" width="16" height="16" fill={tripped ? "none" : C.ok} stroke={tripped ? C.lim : C.ok} />
          <text x="338" y="56" textAnchor="middle" className="sldS" fill={tripped ? C.lim : C.dim}>
            POI BKR {tripped ? "OPEN" : "CLOSED"}
          </text>
          <path d="M480 14 l30 0 m-30 12 l30 0 m-30 12 l30 0 M480 14 l-8 24 M510 14 l8 24" stroke={C.dim} fill="none" />
          <text x="495" y="60" textAnchor="middle" className="sldS">345 kV GRID</text>
        </g>
        <line x1="64" y1="32" x2="120" y2="32" stroke={C.faint} strokeWidth="2" />
        <line x1="178" y1="32" x2="239" y2="32" stroke={C.faint} strokeWidth="2" />
        <line x1="257" y1="32" x2="330" y2="32" stroke={C.faint} strokeWidth="2" />
        <line x1="346" y1="32" x2="480" y2="32" stroke={C.faint} strokeWidth="2" />
        {flow && !tripped && (
          <g className="flowAnim" style={{ animationDuration: dur + "s" }}>
            <line x1="64" y1="32" x2="480" y2="32" stroke={C.p} strokeWidth="2.5" strokeDasharray="4 14" />
          </g>
        )}
      </svg>
      <div className="sldRead">
        <Read label="ACTIVE" val={p.toFixed(1)} unit="MW" color={C.p} />
        <Read label="REACTIVE" val={(q >= 0 ? "+" : "") + q.toFixed(1)} unit="MVAr" color={C.q} />
        <Read label="POI VOLT" val={vPoi.toFixed(3)} unit={"pu · " + (vPoi * 345).toFixed(0) + " kV"} color={C.volt} />
        <Read label="FREQ" val={f.toFixed(3)} unit="Hz" color={C.freq} />
        <div className={"statusLamp " + (tripped ? "trip" : "ok")}>{tripped ? "TRIPPED" : "ON-LINE"}</div>
      </div>
    </div>
  );
}
const Read = ({ label, val, unit, color }) => (
  <div className="read">
    <div className="readLabel">{label}</div>
    <div className="readVal" style={{ color }}>{val}<span className="readUnit"> {unit}</span></div>
  </div>
);

/* ---------------- Controls ---------------- */
const Slider = ({ label, value, min, max, step, unit, onChange, fmt, info, disabled }) => (
  <label className={"ctl" + (disabled ? " disabled" : "")}>
    <span className="ctlLabel">
      <span className="ctlLabelText">{label}{info && (
        <span className="infoDot" tabIndex={0}>i<span className="tip">{info}</span></span>
      )}</span>
      <b>{(fmt || (v => v))(value)}{unit}</b>
    </span>
    <input type="range" min={min} max={max} step={step} value={value} disabled={disabled}
      onChange={e => onChange(parseFloat(e.target.value))} />
  </label>
);
const Seg = ({ options, value, onChange }) => (
  <div className="seg">
    {options.map(o => (
      <button key={o.v} className={"segBtn" + (value === o.v ? " on" : "")} onClick={() => onChange(o.v)}>{o.l}</button>
    ))}
  </div>
);

/* ---------------- App ---------------- */
const TABS = [
  { id: "freq", label: "MW / Frequency" },
  { id: "volt", label: "Voltage Control" },
  { id: "vrt", label: "Voltage Ride-Through" },
  { id: "frt", label: "Frequency Ride-Through" },
  { id: "day", label: "Full Day Sim" },
  { id: "pid", label: "PID Tuning" },
];

const ABOUT = {
  freq: "The plant can only produce as much as the sun allows, and the PPC may also be told to hold below that by the operator or ISO. It runs at whichever is lower. When grid frequency drifts outside the deadband, primary frequency response (PFR) nudges output up or down along the droop curve within seconds — but it can only push output up if some headroom was held back in reserve.",
  volt: "The PPC regulates the 345 kV point of interconnection using inverter reactive capability. AVR holds a voltage setpoint, Volt-VAR follows a droop curve around it, power-factor mode ties MVAr to MW, and fixed-Q holds a constant value. How much each MVAr moves the voltage depends on grid strength (SCR).",
  vrt: "During transmission faults the plant must not trip while voltage stays inside the PRC-024 envelope (dashed red boundary). It injects reactive current proportional to the voltage depression (K-factor), which limits the active current it can carry, then restores MW after the fault clears.",
  frt: "The plant must stay connected through frequency excursions inside the PRC-024 frequency envelope while providing droop response. Trips are only permitted — and expected — beyond the envelope boundaries.",
  day: "A combined storyline. Your settings from the other tabs apply here: curtailment limit, voltage control mode, droop, and K-factor all shape how the plant handles the day.",
  pid: "Inside the PPC, a feedback controller continuously adjusts a command to hold a measured value at its setpoint. This tab lets you tune that loop. A setpoint step is applied; the controller drives the plant to follow it through a realistic communication delay and noisy measurement. Pick the loop (voltage or active power), choose the controller type, and adjust the gains to tame overshoot, oscillation, and offset. Each scenario starts you with a tuning that misbehaves — your job is to tune it out.",
};

const VMODE_NOTE = {
  AVR: "Holds the POI at a fixed voltage setpoint, automatically injecting or absorbing MVAr to keep it there — a thermostat for voltage. Strongest support, but can hit its reactive limit on a weak grid.",
  VoltVAR: "Follows a droop curve: the further voltage strays from setpoint, the more MVAr it commits. Shares voltage support with other plants without fighting them; a deadband ignores small wiggles.",
  PF: "Ties reactive output to active output at a fixed ratio. Q follows MW up and down — so when a cloud cuts output, voltage support fades with it. Simple and predictable, but not voltage-aware.",
  Q: "Holds a constant MVAr value regardless of voltage or MW. Fits a scheduled reactive dispatch from the ISO, but provides no automatic voltage regulation.",
};

const PIDLOOP_NOTE = {
  voltage: "Voltage loop: the controller commands reactive power (MVAr) to hold POI voltage at setpoint. On a weaker grid each MVAr moves voltage more, so the loop is touchier.",
  power: "Active-power loop: the controller commands plant MW to track a dispatch setpoint (AGC-style). The command is the megawatt target the inverters follow.",
};

const PIDTYPE_NOTE = {
  P: "Proportional only: command ∝ present error. Fast, but always settles short of target — proportional action alone can't remove steady-state error.",
  PI: "Proportional + Integral: P gives speed, I accumulates past error to drive offset to zero. This is the workhorse for solar PPCs — fast, accurate, and robust.",
  PID: "Adds Derivative, which reacts to the rate of change of the measurement. In theory it adds damping — but on a noisy solar signal it amplifies that noise into command chatter and reacts badly to comms delay. Since PPC processes are already well-damped and I removes offset, the derivative adds risk with little benefit. That's why real solar PPCs run PI, not full PID.",
};

export default function App() {
  const [tab, setTab] = useState("freq");
  const [scenIdx, setScenIdx] = useState({ freq: 0, volt: 0, vrt: 0, frt: 0, day: 0, pid: 0 });
  const [running, setRunning] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [showGC, setShowGC] = useState(false);
  const [, force] = useReducer(x => x + 1, 0);

  const [gc, setGc] = useState({ droop: 5, deadband: 36, pfReq: 0.95, ramp: 60, scr: 5, vrtK: 2 });
  const [ctlFreq, setCtlFreq] = useState({ limit: 250 });
  const [ctlVolt, setCtlVolt] = useState({ mode: "AVR", vset: 1.0, pfset: 0.98, pfLead: false, qset: 0, vvDb: 0.008, vvDroop: 0.04 });
  const [pid, setPid] = useState({ rate: 0, aw: true, ...SCEN.pid[0].preset });

  const scen = SCEN[tab][scenIdx[tab]];
  const simRef = useRef(null);
  const simTab = useRef(tab);
  // Build the sim on first render, and re-build synchronously the instant the tab changes —
  // before the [tab] effect runs — so a frame never renders the new tab against the old sim's
  // (differently-shaped) history. The effect still runs afterward to load presets / reset playback.
  if (!simRef.current || simTab.current !== tab) {
    simRef.current = tab === "pid" ? initPID(scen, pid) : initSim(scen, gc, { ...ctlFreq, ...ctlVolt });
    simTab.current = tab;
  }
  const refs = useRef({});
  refs.current = { gc, ctlFreq, ctlVolt, pid, scen, tab, speed };

  const reset = () => {
    const r = refs.current;
    if (r.tab === "pid") simRef.current = initPID(r.scen, r.pid);
    else simRef.current = initSim(r.scen, r.gc, { ...r.ctlFreq, ...r.ctlVolt });
    setRunning(false); force();
  };
  useEffect(() => {
    const r = refs.current;
    if (r.tab === "pid") {
      const preset = { rate: 0, aw: true, ...SCEN.pid[scenIdx.pid].preset };
      setPid(preset);                          // load preset into the live sliders
      simRef.current = initPID(r.scen, preset); // init from the same object (no stale-state race)
    } else {
      simRef.current = initSim(r.scen, r.gc, { ...r.ctlFreq, ...r.ctlVolt });
    }
    setRunning(false); force();
    /* eslint-disable-next-line */
  }, [tab, scenIdx[tab]]);

  useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => {
      const r = refs.current;
      const s = simRef.current;
      const dtWall = 0.05 * r.speed;
      const sub = Math.max(1, Math.ceil(dtWall / 0.05));
      const dt = dtWall / sub;
      if (r.tab === "pid") {
        for (let i = 0; i < sub; i++) stepPID(s, dt, r.scen, r.pid);
      } else {
        const ctl = { ...r.ctlFreq, ...r.ctlVolt };
        for (let i = 0; i < sub; i++) stepSim(s, dt, r.scen, r.gc, ctl);
      }
      if (s.done) setRunning(false);
      force();
    }, 50);
    return () => clearInterval(iv);
  }, [running]);

  const s = simRef.current;
  const hist = s.hist;
  const last = hist[hist.length - 1] || { f: 60, vPoi: 1, vSrc: 1, p: 0, q: 0, pAvail: 0, reserve: 0, pf: 1, iq: 0, ip: 0, qCap: 82 };
  const qMaxGC = PAC * Math.tan(Math.acos(Math.min(gc.pfReq, 0.9999)));
  const xMax = scen.dur;
  const markers = (s.tripped && s.tripT != null)
    ? [...(scen.notes || []), { t: s.tripT, trip: true }]
    : (scen.notes || []);

  /* VRT envelopes anchored at eventT, derived from the trip bands themselves */
  const lvEnv = scen.eventT != null ? envFromBands(LV_BANDS, scen.eventT, xMax, 0) : null;
  const hvEnv = scen.eventT != null ? envFromBands(HV_BANDS, scen.eventT, xMax, 1.3) : null;

  const charts = () => {
    if (tab === "freq" || tab === "frt") {
      return (
        <>
          <Chart title="GRID FREQUENCY" unit=" Hz" xMax={xMax} yMin={tab === "frt" ? 57 : 59.4} yMax={tab === "frt" ? 62 : 60.6}
            info={tab === "frt" ? "PRC-024 ride-through: the plant must stay connected near 60 Hz. The further frequency strays, the less time it may remain before a trip is allowed — past 59.4 / 60.6 Hz: up to 180 s; past 58.4 / 61.6 Hz: 30 s; past 57.8 / 61.8 Hz: trips immediately." : null}
            hist={hist} markers={markers} fmt={v => v.toFixed(2)}
            bands={[[60 - gc.deadband / 1000, 60 + gc.deadband / 1000, "rgba(16,185,129,0.09)"]]}
            hLines={tab === "frt" ? [{ v: 57.8, label: "no-trip floor 57.8", color: C.lim }, { v: 61.8, label: "no-trip ceiling 61.8", color: C.lim }] :
              [{ v: 60, label: "", color: C.faint }, { v: 60 + gc.deadband / 1000, label: `PFR deadband ±${gc.deadband} mHz`, color: C.ok }]}
            series={[{ key: "f", color: C.freq, label: "Frequency" }]} />
          <Chart title="ACTIVE POWER" unit=" MW" xMax={xMax} yMin={0} yMax={270} hist={hist} markers={markers} fmt={v => v.toFixed(0)}
            series={[
              { key: "p", color: C.p, label: "Output" },
              { key: "pAvail", color: C.pAvail, label: "Available", dash: true },
              { key: "limit", color: C.warn, label: "Limit", dash: true, w: 1.2 },
            ]} />
          <Chart title="PFR RESERVE (HEADROOM)" unit=" MW" xMax={xMax} yMin={0} yMax={150} hist={hist} markers={markers} fmt={v => v.toFixed(0)}
            series={[{ key: "reserve", color: C.ok, label: "Headroom" }]} />
        </>
      );
    }
    if (tab === "volt") {
      return (
        <>
          <Chart title="VOLTAGE" unit=" pu" xMax={xMax} yMin={0.92} yMax={1.08} hist={hist} markers={markers} fmt={v => v.toFixed(3)}
            hLines={[{ v: ctlVolt.vset, label: "setpoint", color: C.faint }]}
            series={[
              { key: "vPoi", color: C.volt, label: "POI" },
              { key: "vSrc", color: C.dim, label: "System (no support)", dash: true },
            ]} />
          <Chart title="REACTIVE POWER" unit=" MVAr" xMax={xMax} yMin={-Math.ceil(qMaxGC + 15)} yMax={Math.ceil(qMaxGC + 15)} hist={hist} markers={markers} fmt={v => v.toFixed(0)}
            hLines={[{ v: qMaxGC, label: "+Q cap", color: C.lim }, { v: -qMaxGC, label: "−Q cap", color: C.lim }]}
            series={[{ key: "q", color: C.q, label: "Q output" }]} />
          <Chart title="ACTIVE POWER" unit=" MW" xMax={xMax} yMin={0} yMax={270} hist={hist} markers={markers} fmt={v => v.toFixed(0)}
            series={[{ key: "p", color: C.p, label: "MW" }, { key: "pAvail", color: C.pAvail, label: "Available", dash: true }]} />
        </>
      );
    }
    if (tab === "vrt") {
      return (
        <>
          <Chart title="POI VOLTAGE vs PRC-024 NO-TRIP ENVELOPE" unit=" pu" xMax={xMax} yMin={0} yMax={1.3} hist={hist} markers={markers}
            info="PRC-024-style no-trip envelope, simplified for illustration purposes. The plant must keep POI voltage inside the dashed boundaries to stay connected. The deeper the dip (or higher the swell), the less time it's allowed before a trip — low side: below 0.90 pu for 3 s, 0.75 for 2 s, 0.65 for 0.3 s, 0.45 for 0.15 s; the high side mirrors this. The boundary is drawn from the exact thresholds the protection enforces, so crossing it trips the plant."
            envs={[lvEnv, hvEnv]} fmt={v => v.toFixed(2)}
            series={[{ key: "vPoi", color: C.volt, label: "POI voltage", w: 2.2 }]} />
          <Chart title="INVERTER CURRENT (pu of rated)" unit=" pu" xMax={xMax} yMin={-1.1} yMax={1.2} hist={hist} markers={markers} fmt={v => v.toFixed(2)}
            hLines={[{ v: 1.05, label: "current limit", color: C.lim }]}
            series={[{ key: "ip", color: C.p, label: "Active Ip" }, { key: "iq", color: C.q, label: "Reactive Iq" }]} />
          <Chart title="POWER" unit="" xMax={xMax} yMin={-90} yMax={270} hist={hist} markers={markers} fmt={v => v.toFixed(0)}
            series={[{ key: "p", color: C.p, label: "MW" }, { key: "q", color: C.q, label: "MVAr" }]} />
        </>
      );
    }
    if (tab === "pid") {
      const cfg = pidCfg(pid.loop);
      const ph = (hist.length && hist[0].sp !== undefined) ? hist : [];   // guard one-frame mismatch on tab switch
      const m = pidMetrics(ph);
      let termMax = cfg.uMaxPhys * 0.5;
      for (const h of ph) termMax = Math.max(termMax, Math.abs(h.pTerm), Math.abs(h.iTerm), Math.abs(h.dTerm));
      const termY = Math.ceil(termMax * 1.1);
      const ssTol = cfg.loop === "voltage" ? 0.005 : 2;
      return (
        <>
          <div className="metricRow">
            <Read label="OVERSHOOT" val={m ? m.overshoot.toFixed(0) : "—"} unit="%" color={m && m.overshoot > 10 ? C.warn : C.ok} />
            <Read label="SETTLING ±2%" val={m && m.settled ? m.settle.toFixed(1) : "—"} unit={m && m.settled ? "s" : "not settled"} color={m && m.settled ? C.text : C.lim} />
            <Read label="RISE 10–90%" val={m && m.rise != null ? m.rise.toFixed(1) : "—"} unit="s" color={C.text} />
            <Read label="STEADY-STATE ERR" val={m ? (cfg.loop === "voltage" ? m.ssErr.toFixed(3) : m.ssErr.toFixed(1)) : "—"} unit={cfg.pvUnit.trim()} color={m && Math.abs(m.ssErr) > ssTol ? C.lim : C.ok} />
          </div>
          <Chart title={cfg.loop === "voltage" ? "POI VOLTAGE vs SETPOINT" : "ACTIVE POWER vs SETPOINT"} unit={cfg.pvUnit} xMax={xMax} yMin={cfg.pvMin} yMax={cfg.pvMax} hist={ph} markers={markers} fmt={cfg.pvFmt}
            series={[
              { key: "pvMeas", color: C.dim, label: "Measured", w: 1 },
              { key: "pv", color: cfg.color, label: "Process variable", w: 2.2 },
              { key: "sp", color: C.faint, label: "Setpoint", dash: true },
            ]} />
          <Chart title="CONTROLLER OUTPUT (COMMAND)" unit={cfg.uUnit} xMax={xMax} yMin={cfg.uMinPhys < 0 ? cfg.uMinPhys * 1.15 : -8} yMax={cfg.uMaxPhys * 1.15} hist={ph} markers={markers} fmt={cfg.uFmt}
            hLines={[{ v: cfg.uMaxPhys, label: "actuator max", color: C.lim }, { v: cfg.uMinPhys, label: "actuator min", color: C.lim }]}
            series={[{ key: "u", color: C.accent, label: "Command", w: 1.6 }]} />
          <Chart title="CONTROLLER TERM CONTRIBUTIONS" unit={cfg.uUnit} xMax={xMax} yMin={-termY} yMax={termY} hist={ph} markers={markers} fmt={cfg.uFmt}
            info="How each term contributes to the command. P reacts to the present error, I accumulates past error to erase offset, and D reacts to the rate of change. On a noisy signal the D trace (red) explodes while P and I stay calm — that is the case against derivative on a solar PPC."
            series={[
              { key: "pTerm", color: C.p, label: "P" },
              { key: "iTerm", color: C.ok, label: "I" },
              { key: "dTerm", color: C.lim, label: "D" },
            ]} />
        </>
      );
    }
    // day
    return (
      <>
        <Chart title="ACTIVE POWER" unit=" MW" xMax={xMax} yMin={0} yMax={270} hist={hist} markers={markers} fmt={v => v.toFixed(0)}
          series={[
            { key: "p", color: C.p, label: "Output" },
            { key: "pAvail", color: C.pAvail, label: "Available", dash: true },
            { key: "limit", color: C.warn, label: "Limit", dash: true, w: 1.2 },
          ]} />
        <Chart title="VOLTAGE" unit=" pu" xMax={xMax} yMin={0.2} yMax={1.15} hist={hist} markers={markers} fmt={v => v.toFixed(3)}
          series={[{ key: "vPoi", color: C.volt, label: "POI" }, { key: "vSrc", color: C.dim, label: "System", dash: true }]} />
        <Chart title="GRID FREQUENCY" unit=" Hz" xMax={xMax} yMin={59.4} yMax={60.4} hist={hist} markers={markers} fmt={v => v.toFixed(2)}
          bands={[[60 - gc.deadband / 1000, 60 + gc.deadband / 1000, "rgba(16,185,129,0.09)"]]}
          hLines={[{ v: 60 + gc.deadband / 1000, label: `PFR deadband ±${gc.deadband} mHz`, color: C.ok }]}
          series={[{ key: "f", color: C.freq, label: "Frequency" }]} />
        <Chart title="REACTIVE POWER" unit=" MVAr" xMax={xMax} yMin={-Math.ceil(qMaxGC + 15)} yMax={Math.ceil(qMaxGC + 15)} hist={hist} markers={markers} fmt={v => v.toFixed(0)}
          hLines={[{ v: qMaxGC, label: "+Q cap", color: C.lim }, { v: -qMaxGC, label: "−Q cap", color: C.lim }]}
          series={[{ key: "q", color: C.q, label: "Q output" }]} />
      </>
    );
  };

  const controls = () => {
    if (tab === "freq" || tab === "frt" || tab === "day") {
      return (
        <>
          <Slider label="Active power limit" value={ctlFreq.limit} min={0} max={250} step={5} unit=" MW"
            onChange={v => setCtlFreq({ limit: v })} fmt={v => v.toFixed(0)} />
          <Slider label="Droop" value={gc.droop} min={2} max={10} step={0.5} unit="%"
            onChange={v => setGc({ ...gc, droop: v })} />
          <Slider label="Deadband" value={gc.deadband} min={0} max={100} step={2} unit=" mHz"
            onChange={v => setGc({ ...gc, deadband: v })} fmt={v => v.toFixed(0)} />
          <Slider label="Ramp rate" value={gc.ramp} min={10} max={250} step={10} unit=" MW/min"
            onChange={v => setGc({ ...gc, ramp: v })} fmt={v => v.toFixed(0)} disabled={!scen.rampDemo}
            info={scen.rampDemo
              ? "Grid-code limit: the fastest the plant may change active power during normal dispatch and recovery. Lower it and watch curtailment restoration slow down. Security curtailments still execute fast downward; fast frequency response (PFR) is not ramp-limited."
              : "Inactive in this scenario — there's no upward dispatch ramp here to shape (frequency response is a separate fast path, and curtailment downward is always fast). Run “ISO curtailment order” or the Full Day Sim and lower it to watch restoration slow down."} />
          <div className="hint">Tip: curtail below available power to create headroom, then run the under-frequency scenario to see upward PFR.</div>
        </>
      );
    }
    if (tab === "volt") {
      return (
        <>
          <div className="ctlLabel" style={{ marginBottom: 6 }}>Control mode</div>
          <Seg value={ctlVolt.mode} onChange={v => setCtlVolt({ ...ctlVolt, mode: v })}
            options={[{ v: "AVR", l: "AVR" }, { v: "VoltVAR", l: "Volt-VAR" }, { v: "PF", l: "Power factor" }, { v: "Q", l: "Fixed Q" }]} />
          <div className="modeNote">{VMODE_NOTE[ctlVolt.mode]}</div>
          {(ctlVolt.mode === "AVR" || ctlVolt.mode === "VoltVAR") && (
            <Slider label="Voltage setpoint" value={ctlVolt.vset} min={0.95} max={1.05} step={0.005} unit=" pu"
              onChange={v => setCtlVolt({ ...ctlVolt, vset: v })} fmt={v => v.toFixed(3)} />
          )}
          {ctlVolt.mode === "VoltVAR" && (
            <>
              <Slider label="Deadband" value={ctlVolt.vvDb} min={0} max={0.02} step={0.002} unit=" pu"
                onChange={v => setCtlVolt({ ...ctlVolt, vvDb: v })} fmt={v => v.toFixed(3)} />
              <Slider label="V-Q droop (full Q over)" value={ctlVolt.vvDroop} min={0.02} max={0.08} step={0.005} unit=" pu"
                onChange={v => setCtlVolt({ ...ctlVolt, vvDroop: v })} fmt={v => v.toFixed(3)} />
            </>
          )}
          {ctlVolt.mode === "PF" && (
            <>
              <Slider label="Power factor setpoint" value={ctlVolt.pfset} min={0.9} max={1.0} step={0.005}
                unit={ctlVolt.pfLead ? " lead" : " lag"} onChange={v => setCtlVolt({ ...ctlVolt, pfset: v })} fmt={v => v.toFixed(3)} />
              <Seg value={ctlVolt.pfLead} onChange={v => setCtlVolt({ ...ctlVolt, pfLead: v })}
                options={[{ v: false, l: "Lagging (inject Q)" }, { v: true, l: "Leading (absorb Q)" }]} />
            </>
          )}
          {ctlVolt.mode === "Q" && (
            <Slider label="Q setpoint" value={ctlVolt.qset} min={-Math.round(qMaxGC)} max={Math.round(qMaxGC)} step={1} unit=" MVAr"
              onChange={v => setCtlVolt({ ...ctlVolt, qset: v })} fmt={v => v.toFixed(0)} />
          )}
          <div className="hint">Try the sag scenario in PF mode, then again in AVR — watch the POI trace.</div>
        </>
      );
    }
    if (tab === "pid") {
      return (
        <>
          <div className="ctlLabel" style={{ marginBottom: 6 }}>Control loop</div>
          <Seg value={pid.loop} onChange={v => setPid({ ...pid, loop: v })}
            options={[{ v: "voltage", l: "Voltage" }, { v: "power", l: "Active power" }]} />
          <div className="modeNote">{PIDLOOP_NOTE[pid.loop]}</div>
          <div className="ctlLabel" style={{ margin: "10px 0 6px" }}>Controller</div>
          <Seg value={pid.mode} onChange={v => setPid({ ...pid, mode: v })}
            options={[{ v: "P", l: "P" }, { v: "PI", l: "PI" }, { v: "PID", l: "PID" }]} />
          <div className="modeNote">{PIDTYPE_NOTE[pid.mode]}</div>
          <Slider label="Proportional gain (Kp)" value={pid.kp} min={0} max={6} step={0.1} unit=""
            onChange={v => setPid({ ...pid, kp: v })} fmt={v => v.toFixed(1)}
            info="How hard the controller reacts to the present error. Higher = faster, but too high causes overshoot and oscillation." />
          <Slider label="Integral gain (Ki)" value={pid.ki} min={0} max={4} step={0.05} unit="/s"
            onChange={v => setPid({ ...pid, ki: v })} fmt={v => v.toFixed(2)} disabled={pid.mode === "P"}
            info="Accumulates past error to drive the steady-state offset to zero. Too high adds overshoot and sluggish settling." />
          <Slider label="Derivative gain (Kd)" value={pid.kd} min={0} max={1.5} step={0.05} unit="s"
            onChange={v => setPid({ ...pid, kd: v })} fmt={v => v.toFixed(2)} disabled={pid.mode !== "PID"}
            info="Reacts to the rate of change of the measurement. Adds damping in theory, but amplifies measurement noise — which is why solar PPCs normally leave it at zero (PI)." />
          <Slider label="Measurement noise" value={pid.noise} min={0} max={0.05} step={0.005} unit=""
            onChange={v => setPid({ ...pid, noise: v })} fmt={v => (v * 100).toFixed(1) + "%"}
            info="Random noise on the measured signal (as % of span). Realistic for field measurements; it's what the derivative term amplifies." />
          <Slider label="Comms / control delay" value={pid.delay} min={0} max={0.6} step={0.05} unit=" s"
            onChange={v => setPid({ ...pid, delay: v })} fmt={v => v.toFixed(2)}
            info="Round-trip latency between measuring and acting (SCADA / inverter comms). Delay is the enemy of high gain — it pushes an aggressive loop toward oscillation and instability." />
          <Slider label="Command rate limit" value={pid.rate} min={0} max={1} step={0.05} unit=""
            onChange={v => setPid({ ...pid, rate: v })} fmt={v => v === 0 ? "off" : (v * 100).toFixed(0) + " %/s"}
            info="How fast the command itself may change (a slew-rate limit, like the plant's MW ramp rate), as % of full range per second. With an aggressive integral this causes windup — see the windup scenario." />
          <div className="ctlLabel" style={{ margin: "10px 0 6px" }}>Anti-windup</div>
          <Seg value={pid.aw} onChange={v => setPid({ ...pid, aw: v })}
            options={[{ v: true, l: "On" }, { v: false, l: "Off" }]} />
          <div className="modeNote">{pid.aw
            ? "On: while the command is saturated or rate-limited, integration pauses so error can't pile up — prevents windup overshoot. This is standard practice."
            : "Off: the integral keeps accumulating even when the command can't keep up, so it overshoots badly on arrival (windup)."}</div>
          <div className="hint">Each scenario starts with a tuning that misbehaves — adjust the controls to tune it out. Switch scenarios to load a different starting tuning; RESET restarts with your current gains.</div>
        </>
      );
    }
    // vrt
    return (
      <>
        <Slider label="Reactive current gain (K-factor)" value={gc.vrtK} min={0} max={6} step={0.5} unit="×"
          onChange={v => setGc({ ...gc, vrtK: v })} />
        <Slider label="Grid strength (SCR)" value={gc.scr} min={2} max={20} step={1} unit="×"
          onChange={v => setGc({ ...gc, scr: v })} fmt={v => v.toFixed(0)} />
        <div className="hint">Higher K injects more reactive current during the fault — in a weak grid (low SCR) it visibly lifts the POI voltage.</div>
      </>
    );
  };

  return (
    <div className="app">
      <style>{CSS}</style>
      <header className="hdr">
        <div className="hdrTop">
          <div className="brand">
            <div className="brandText">
              <div className="title">SimuPlant<span className="byline">by Thomas Ashworth</span></div>
              <div className="titleSub">PV Plant · PPC Behavior Simulator</div>
              <div className="meta">250 MWac · 325 MWdc (1.3 ILR) · 345 kV POI · ±{qMaxGC.toFixed(0)} MVAr ({gc.pfReq.toFixed(2)} PF) · simplified IEEE 2800 / PRC-024</div>
            </div>
          </div>
          <button className={"gcBtn" + (showGC ? " on" : "")} onClick={() => setShowGC(!showGC)}>SET GRID CODE ▾</button>
        </div>
        {showGC && (
          <div className="gcPanel">
            <Slider label="TSP power-factor requirement" value={gc.pfReq} min={0.9} max={1.0} step={0.005} unit=" PF"
              onChange={v => setGc({ ...gc, pfReq: v })} fmt={v => v.toFixed(3)} />
            <Slider label="Droop" value={gc.droop} min={2} max={10} step={0.5} unit="%" onChange={v => setGc({ ...gc, droop: v })}
              info="Sets how strongly output responds to frequency. A 5% droop means a full 5% frequency swing moves output by 100%. Lower % = stronger, faster response." />
            <Slider label="Deadband" value={gc.deadband} min={0} max={100} step={2} unit=" mHz"
              onChange={v => setGc({ ...gc, deadband: v })} fmt={v => v.toFixed(0)}
              info="A frequency window around 60 Hz where the plant holds steady and takes no action — it stops normal grid noise from causing constant small adjustments." />
            <Slider label="Ramp rate" value={gc.ramp} min={10} max={250} step={10} unit=" MW/min"
              onChange={v => setGc({ ...gc, ramp: v })} fmt={v => v.toFixed(0)}
              info="The fastest the plant is allowed to change active power, in MW per minute, during normal dispatch and recovery. Security curtailments can override this downward." />
            <Slider label="Grid strength at POI (SCR)" value={gc.scr} min={2} max={20} step={1} unit="×"
              onChange={v => setGc({ ...gc, scr: v })} fmt={v => v.toFixed(0)}
              info="Short-Circuit Ratio — how 'stiff' the grid is at the connection point. High SCR means voltage barely moves when the plant injects MVAr; low SCR makes voltage swing much more." />
            <Slider label="Ride-through K-factor" value={gc.vrtK} min={0} max={6} step={0.5} unit="×"
              onChange={v => setGc({ ...gc, vrtK: v })}
              info="Ride-through reactive gain. During a voltage dip it sets how much reactive current the plant injects per unit of dip to help prop voltage back up." />
            <div className="hint">These are the grid-code defaults. Reactive capability ±{qMaxGC.toFixed(0)} MVAr follows from the PF requirement, and SCR sets how much each MVAr moves POI voltage (ΔV ≈ Q ÷ {gc.scr * PAC} MVA). The SETPOINTS panel on each tab adjusts the same values live.</div>
          </div>
        )}
      </header>

      {tab !== "pid" && (
        <section className="sldCard">
          <div className="panelTitle">ONE-LINE DIAGRAM</div>
          <SLD p={last.p} q={last.q} vPoi={last.vPoi} tripped={s.tripped} f={last.f} />
        </section>
      )}

      <nav className="tabs">
        {TABS.map(t2 => (
          <button key={t2.id} className={"tab" + (tab === t2.id ? " on" : "")} onClick={() => setTab(t2.id)}>{t2.label}</button>
        ))}
      </nav>

      <div className="about">{ABOUT[tab]}</div>

      <main className="main">
        <aside className="side">
          <div className="panel">
            <div className="panelTitle">SCENARIO</div>
            <select className="select" value={scenIdx[tab]} onChange={e => setScenIdx({ ...scenIdx, [tab]: +e.target.value })}>
              {SCEN[tab].map((sc, i) => <option key={sc.id} value={i}>{sc.name}</option>)}
            </select>
            <div className="scenDesc">{scen.desc}</div>
            <div className="playRow">
              <button className="playBtn" onClick={() => { if (s.done) reset(); setRunning(r => !r); }}>
                {running ? "❚❚ PAUSE" : s.done ? "↻ REPLAY" : "▶ PLAY"}
              </button>
              <button className="rstBtn" onClick={reset}>RESET</button>
            </div>
            <div className="speedRow">
              <span className="ctlLabel">Speed</span>
              <Seg value={speed} onChange={setSpeed} options={[{ v: 1, l: "1×" }, { v: 2, l: "2×" }, { v: 4, l: "4×" }, { v: 8, l: "8×" }]} />
            </div>
            <div className="timebar"><div className="timefill" style={{ width: (s.t / xMax * 100) + "%" }} /></div>
            <div className="timeText">t = {s.t.toFixed(1)} s / {xMax} s</div>
          </div>
          <div className="panel">
            <div className="panelTitle">SETPOINTS</div>
            {controls()}
          </div>
        </aside>

        <section className="charts">
          {charts()}
        </section>

        <aside className="rail">
          <div className="panel logPanel">
            <div className="panelTitle">EVENT LOG<span className="logClock">t = {s.t.toFixed(1)}s</span></div>
            <div className="log">
              {s.log.length === 0 && <div className="logEmpty">Press play — controller and grid events will appear here.</div>}
              {[...s.log].reverse().map((e, i) => (
                <div key={i} className={"logRow " + e.kind}>
                  <span className="logT">{e.t.toFixed(1)}s</span>{e.text}
                </div>
              ))}
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}

/* ---------------- CSS ---------------- */
const CSS = `
${JAKARTA_FONT_CSS}
* { box-sizing: border-box; }
.app { min-height: 100vh; background: ${C.bg}; color: ${C.text};
  font-family: 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif; font-size: 13px; padding: 16px;
  -webkit-font-smoothing: antialiased; }
.hdr { background: ${C.panel}; border: 1px solid ${C.line}; border-radius: 14px; padding: 14px 16px; box-shadow: 0 1px 3px rgba(16,24,40,0.04); }
.sldCard { background: ${C.panel}; border: 1px solid ${C.line}; border-radius: 14px; padding: 14px 16px; margin-top: 12px; box-shadow: 0 1px 3px rgba(16,24,40,0.04); }
.hdrTop { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; flex-wrap: wrap; }
.brand { display: flex; align-items: center; gap: 13px; }
.title { font-size: 22px; font-weight: 800; letter-spacing: -0.4px; color: #3c4456; line-height: 1.1; }
.byline { color: ${C.dim}; font-size: 12px; font-weight: 600; letter-spacing: 0; margin-left: 9px; }
.titleSub { color: ${C.accent}; font-size: 12.5px; font-weight: 700; letter-spacing: 0.3px; margin-top: 2px; }
.meta { color: ${C.dim}; font-size: 11px; margin-top: 4px; font-weight: 500; }
.gcBtn { background: rgba(113,102,240,0.08); border: 1px solid rgba(113,102,240,0.4); color: ${C.accent}; padding: 7px 13px;
  border-radius: 8px; font-family: inherit; font-size: 11px; font-weight: 700; letter-spacing: 0.5px; cursor: pointer; }
.gcBtn:hover { background: rgba(113,102,240,0.14); }
.gcBtn.on { background: ${C.accent}; color: #fff; border-color: ${C.accent}; }
.gcPanel { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 8px 18px;
  border-top: 1px solid ${C.line}; margin-top: 12px; padding-top: 12px; }
.sld { display: flex; gap: 14px; align-items: center; margin-top: 8px; flex-wrap: wrap; }
.sldSvg { width: 100%; max-width: 560px; height: 64px; flex: 1 1 320px; }
.sldT { font-size: 9px; fill: ${C.text}; font-family: inherit; font-weight: 600; }
.sldS { font-size: 8px; fill: ${C.dim}; font-family: inherit; }
.flowAnim line { animation-name: flow; animation-timing-function: linear; animation-iteration-count: infinite; animation-duration: inherit; }
@keyframes flow { to { stroke-dashoffset: -18; } }
@media (prefers-reduced-motion: reduce) { .flowAnim line { animation: none; } }
.sldRead { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; }
.read { min-width: 86px; }
.readLabel { font-size: 9px; color: ${C.dim}; letter-spacing: 1px; font-weight: 600; text-transform: uppercase; }
.readVal { font-size: 18px; font-weight: 700; letter-spacing: -0.3px; }
.readUnit { font-size: 9px; color: ${C.dim}; font-weight: 500; }
.metricRow { display: flex; gap: 18px; align-items: center; flex-wrap: wrap; background: ${C.panel}; border: 1px solid ${C.line}; border-radius: 12px; padding: 12px 16px; box-shadow: 0 1px 3px rgba(16,24,40,0.04); }
.statusLamp { padding: 5px 12px; border-radius: 999px; font-size: 11px; letter-spacing: 0.5px; font-weight: 700; }
.statusLamp.ok { background: rgba(16,185,129,0.12); color: #059669; border: 1px solid rgba(16,185,129,0.35); }
.statusLamp.trip { background: rgba(239,68,68,0.12); color: ${C.lim}; border: 1px solid ${C.lim}; animation: blink 1s step-end infinite; }
@keyframes blink { 50% { opacity: 0.45; } }
.tabs { display: flex; gap: 6px; margin: 14px 0 0; flex-wrap: wrap; }
.tab { background: ${C.panel}; border: 1px solid ${C.line}; color: ${C.dim};
  padding: 8px 15px; border-radius: 9px; font-weight: 600;
  font-size: 13px; letter-spacing: 0.2px; cursor: pointer; }
.tab:hover { color: ${C.text}; }
.tab.on { background: ${C.accent}; color: #fff; border-color: ${C.accent}; box-shadow: 0 2px 8px rgba(113,102,240,0.25); }
.about { background: ${C.panel}; border: 1px solid ${C.line}; padding: 11px 15px; color: ${C.dim};
  font-size: 11.5px; line-height: 1.55; border-radius: 12px; margin-top: 10px; box-shadow: 0 1px 3px rgba(16,24,40,0.04); }
.main { display: grid; grid-template-columns: 300px minmax(0, 1fr) 320px; gap: 12px; margin-top: 12px; }
.side { display: flex; flex-direction: column; gap: 12px; }
.panel { background: ${C.panel}; border: 1px solid ${C.line}; border-radius: 12px; padding: 14px; box-shadow: 0 1px 3px rgba(16,24,40,0.04); }
.panelTitle { font-weight: 700; letter-spacing: 1px; font-size: 11px; text-transform: uppercase;
  color: ${C.dim}; margin-bottom: 10px; }
.select { width: 100%; background: ${C.panel}; color: ${C.text}; border: 1px solid ${C.line};
  border-radius: 8px; padding: 8px 9px; font-family: inherit; font-size: 12px; font-weight: 500; cursor: pointer; }
.scenDesc { color: ${C.dim}; font-size: 11px; line-height: 1.5; margin: 8px 0; }
.playRow { display: flex; gap: 8px; }
.playBtn { flex: 1; background: ${C.accent}; color: #fff; border: none; border-radius: 8px; padding: 10px;
  font-family: inherit; font-weight: 700; font-size: 14px; letter-spacing: 0.5px; cursor: pointer; box-shadow: 0 2px 8px rgba(113,102,240,0.25); }
.playBtn:hover { filter: brightness(1.06); }
.rstBtn { background: ${C.panel}; border: 1px solid ${C.line}; color: ${C.dim}; border-radius: 8px; padding: 9px 13px;
  font-family: inherit; font-size: 11px; font-weight: 600; cursor: pointer; }
.rstBtn:hover { color: ${C.text}; border-color: ${C.dim}; }
.speedRow { display: flex; justify-content: space-between; align-items: center; margin-top: 12px; }
.timebar { height: 5px; background: ${C.panel2}; border-radius: 999px; margin-top: 12px; overflow: hidden; }
.timefill { height: 100%; background: ${C.accent}; transition: width 0.05s linear; }
.timeText { color: ${C.dim}; font-size: 10px; margin-top: 5px; text-align: right; font-weight: 500; }
.ctl { display: block; margin-bottom: 11px; }
.ctl.disabled input[type=range] { opacity: 0.35; cursor: not-allowed; filter: grayscale(1); }
.ctl.disabled .ctlLabel, .ctl.disabled .ctlLabel b { color: ${C.faint}; }
.ctlLabel { display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: ${C.dim}; margin-bottom: 5px; font-weight: 500; }
.ctlLabel b { color: ${C.text}; font-weight: 700; }
.ctlLabelText { display: inline-flex; align-items: center; }
.infoDot { position: relative; display: inline-flex; align-items: center; justify-content: center;
  width: 13px; height: 13px; margin-left: 5px; border: 1px solid ${C.faint}; border-radius: 50%;
  color: ${C.dim}; font-size: 9px; font-weight: 700; font-style: italic; cursor: help; flex: none; }
.infoDot:hover, .infoDot:focus { border-color: ${C.accent}; color: ${C.accent}; outline: none; }
.infoDot .tip { position: absolute; top: calc(100% + 7px); left: 50%; transform: translateX(-50%);
  width: 212px; background: ${C.panel}; border: 1px solid ${C.line}; border-radius: 8px;
  padding: 9px 11px; color: ${C.text}; font-size: 10.5px; line-height: 1.5; font-weight: 500; font-style: normal;
  box-shadow: 0 8px 24px rgba(16,24,40,0.14); opacity: 0; visibility: hidden; transition: opacity 0.12s;
  z-index: 40; pointer-events: none; }
.infoDot .tip::after { content: ''; position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%);
  border: 5px solid transparent; border-bottom-color: ${C.panel}; }
.infoDot:hover .tip, .infoDot:focus .tip { opacity: 1; visibility: visible; }
.chartInfo .tip { left: 0; transform: none; width: 250px; }
.chartInfo .tip::after { left: 16px; transform: none; }
input[type=range] { width: 100%; accent-color: ${C.accent}; height: 18px; }
.seg { display: flex; gap: 5px; flex-wrap: wrap; margin-bottom: 10px; }
.segBtn { background: ${C.panel}; border: 1px solid ${C.line}; color: ${C.dim}; padding: 6px 11px;
  border-radius: 8px; font-family: inherit; font-size: 11px; font-weight: 600; cursor: pointer; }
.segBtn:hover { color: ${C.text}; }
.segBtn.on { border-color: ${C.accent}; color: ${C.accent}; background: rgba(113,102,240,0.08); }
.hint { color: ${C.dim}; font-size: 10.5px; line-height: 1.5; margin-top: 6px; border-left: 2px solid ${C.line}; padding-left: 9px; }
.modeNote { color: ${C.dim}; font-size: 11px; line-height: 1.55; margin: 8px 0 4px; padding-left: 9px;
  border-left: 2px solid ${C.accent}; }
.charts { display: flex; flex-direction: column; gap: 12px; min-width: 0; }
.chart { background: ${C.panel}; border: 1px solid ${C.line}; border-radius: 12px; padding: 12px 14px 8px; box-shadow: 0 1px 3px rgba(16,24,40,0.04); }
.chartHead { display: flex; justify-content: space-between; align-items: baseline; flex-wrap: wrap; gap: 4px 12px; margin-bottom: 6px; }
.chartTitle { font-weight: 700; letter-spacing: 0.8px; font-size: 11.5px; text-transform: uppercase; color: ${C.dim}; }
.legend { display: flex; gap: 12px; flex-wrap: wrap; }
.legItem { font-size: 10px; color: ${C.dim}; display: inline-flex; align-items: center; gap: 4px; font-weight: 500; }
.legItem b { font-size: 11px; font-weight: 700; }
.swatch { width: 10px; height: 3px; display: inline-block; border-radius: 2px; }
.chartSvg { width: 100%; height: auto; display: block; }
.tick { font-size: 9px; fill: ${C.faint}; font-family: inherit; font-weight: 500; }
.rail { min-width: 0; }
.logPanel { position: sticky; top: 12px; max-height: calc(100vh - 24px); display: flex; flex-direction: column; }
.logPanel .panelTitle { display: flex; justify-content: space-between; align-items: baseline; }
.logClock { font-weight: 600; font-size: 11px; color: ${C.accent}; letter-spacing: 0; }
.log { overflow-y: auto; flex: 1; }
.logEmpty { color: ${C.faint}; font-size: 11px; }
.logRow { font-size: 11px; padding: 4px 0; border-bottom: 1px solid ${C.line}; color: ${C.text}; font-weight: 500; }
.logRow.warn { color: #ea580c; } .logRow.ok { color: #059669; } .logRow.trip { color: ${C.lim}; font-weight: 700; }
.logRow.scen { color: #0d9488; }
.logT { color: ${C.faint}; margin-right: 8px; }
@media (max-width: 1100px) {
  .main { grid-template-columns: 300px minmax(0, 1fr); }
  .rail { grid-column: 1 / -1; }
  .logPanel { position: static; max-height: 200px; }
}
@media (max-width: 880px) {
  .main { grid-template-columns: 1fr; }
  .app { padding: 8px; font-size: 12px; }
  .title { font-size: 18px; }
  .sldRead { gap: 10px; }
}
`;
